import React from "react";
import { motion } from "framer-motion";
import {
  Lightbulb,
  Users,
  PenTool,
  TrendingUp,
  Video,
  Globe,
  Search,
  Mail,
  BarChart,
  Camera,
} from "lucide-react";
import "./Services.css";

const Services = () => {
  const services = [
    {
      title: "Social Media Management",
      icon: <Users className="service-icon" />,
      description:
        "Results-driven social media marketing. The Kynetic Approach: We build channels that your audience loves, creating a community around your brand and driving meaningful engagement.",
      features: [
        "Platform Strategy (IG, LinkedIn, TikTok, YouTube)",
        "Content Calendar & Engagement",
        "Content Creation (Posts, Reels, Videos)",
        "Community Building & Copywriting",
      ],
    },
    {
      title: "Digital Strategy & Consulting",
      icon: <Lightbulb className="service-icon" />,
      description:
        "Success is not an accident. The Kynetic Approach: Before we execute, we plan. We build comprehensive strategies based on data, market trends, and business objectives.",
      features: [
        "Market Analysis & Competitor Audits",
        "Brand Positioning Strategy",
        "Full-funnel Architecture",
        "Revenue Roadmaps",
      ],
    },
    {
      title: "Influencer & Creator Marketing",
      icon: <Users className="service-icon" size={24} strokeWidth={1.5} />,
      description:
        "Kynetic Approach: Brands trust people, not brands. We identify and activate the perfect creator partnerships for your brand goals.",
      features: [
        "Creator Identification & Vetting",
        "Campaign Strategy & Management",
        "User-Generated Content (UGC) Creation",
        "Performance Tracking & Affiliate Management",
      ],
    },
    {
      title: "Branding & Identity Design",
      icon: <PenTool className="service-icon" />,
      description:
        "A brand is not a logo. It's a feeling. The Kynetic Approach: We craft cohesive brand systems that position your business for maximum impact.",
      features: [
        "Logo Design & Visual Identity Systems",
        "Brand Guidelines & Style Guides",
        "Verbal Voice & Messaging Strategy",
        "Rebranding & Refreshes",
      ],
    },
    {
      title: "Performance Marketing (PPC)",
      icon: <TrendingUp className="service-icon" />,
      description:
        "Ad spend that converts, not vanishes. The Kynetic Approach: We build performance marketing systems that eliminate waste and capture revenue.",
      features: [
        "Google Ads (Search & Shopping)",
        "Meta Ads (Facebook & Instagram)",
        "LinkedIn and LinkedIn",
        "Retargeting Funnels",
      ],
    },
    {
      title: "Video Production",
      icon: <Video className="service-icon" />,
      description:
        "Storytelling in motion. The Kynetic Approach is our end-to-end video production that delivers brand-elevating content from concept to final cut.",
      features: [
        "Short-Form Vertical Video (Reels/TikToks)",
        "Customer Testimonials & Case Studies",
        "Event Coverage & Highlight Reels",
        "Motion Graphics & Animation",
      ],
    },
    {
      title: "Web Experience & CRO",
      icon: <Globe className="service-icon" />,
      description:
        "Your digital flagship, engineered for speed. The Kynetic Approach: We build websites that convert visitors into customers.",
      features: [
        "Custom Website Design & Development",
        "Landing Page Optimization",
        "User Experience (UX) Audits",
        "Conversion Rate Optimization (CRO)",
      ],
    },
    {
      title: "Search Engine Optimization (SEO)",
      icon: <Search className="service-icon" />,
      description:
        "Dominance is visible until it's everywhere. The Kynetic Approach: We build SEO frameworks that drive qualified traffic to your website.",
      features: [
        "Technical Site Audits & Architecture",
        "On-Page & Off-Page Optimization",
        "Content Strategy & Creation",
        "Local SEO & Google Business Profile Management",
      ],
    },
    {
      title: "Email Marketing & Automation",
      icon: <Mail className="service-icon" />,
      description:
        "The fortune is in the follow-up. The Kynetic Approach: Setting up the right automated flows that nurture prospects into customers.",
      features: [
        "Platform Setup & Integration",
        "Automated Flows",
        "Newsletter Strategy & Design",
        "List Segmentation & Hygiene",
      ],
    },
    {
      title: "Marketing Operations & AI Integration",
      icon: <BarChart className="service-icon" />,
      description:
        "Efficiency is the ultimate edge. The Kynetic Approach: We eliminate manual grunt work by building connected systems.",
      features: [
        "CRM Setup & Integration (HubSpot/Salesforce)",
        "Data Dashboards & Reporting",
        "ChatGPT & AI Customer Support Setup",
        "Cross-Departmental Reporting",
      ],
    },
    {
      title: "Product Photography",
      icon: <Camera className="service-icon" />,
      description:
        "Visuals sell more than text. The Kynetic Approach: We shoot your products to shine on your target channels.",
      features: [
        "E-commerce White Background",
        "Creative Lifestyle & Contextual Shoots",
        "360 Degree Product Spins",
        "High-End Retouching & Color Correction",
      ],
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  return (
    <main id="main-content" className="services-page">
        <div className="services-content">
          <motion.h1
            className="services-title"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Digital Marketing Services<span className="services-title-dot">.</span>
          </motion.h1>
          <span className="services-title-arrow" aria-hidden="true">
            →
          </span>

          <motion.p
            className="services-subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            If your mind can <span className="highlight">Think It</span>, we can{" "}
            <span className="highlight">Do It</span>!
          </motion.p>

          <motion.p
            className="services-description"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.7 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            From organic growth to cinematic production, we provide the kinetic
            energy your brand needs to move forward.
          </motion.p>
        </div>

        <div className="services-container">
          <div className="services-header">
            <h2 className="services-section-title">
              Comprehensive Capabilities.
              <br />
              <span className="yellow-text">Integrated Impact</span>
            </h2>
          </div>

          <motion.div
            className="services-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
          >
            {services.map((service, index) => (
              <motion.div
                key={index}
                className="service-card"
                variants={itemVariants}
                whileHover={{ y: -10, transition: { duration: 0.2 } }}
              >
                <div className="service-icon-container">{service.icon}</div>
                <h3 className="service-title">{service.title}</h3>
                <p className="service-description">{service.description}</p>
                <ul className="service-features">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="service-feature">
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>
        </div>
    </main>
  );
};

export default Services;
